import { RULE_PRESETS, type SwitchRules } from '@cardengine/shared';
import { useStore } from '../store.js';

interface RulesEditorProps {
  rules: SwitchRules;
  editable: boolean;
}

/** One rank's competing behaviours, as a single choice. */
interface RankRule<K extends keyof SwitchRules> {
  key: K;
  rank: string;
  options: { value: SwitchRules[K]; label: string; hint: string }[];
}

const RANK_RULES = [
  {
    key: 'twos',
    rank: '2',
    options: [
      { value: 'pickupTwo', label: 'Pick up 2', hint: 'The next player takes two cards.' },
      { value: 'none', label: 'Nothing', hint: 'An ordinary card.' },
    ],
  } satisfies RankRule<'twos'>,
  {
    key: 'sevens',
    rank: '7',
    options: [
      { value: 'skip', label: 'Skip', hint: 'The next player misses their turn.' },
      { value: 'mirror', label: 'Mirror', hint: 'The next player must follow the seven’s suit.' },
      { value: 'none', label: 'Nothing', hint: 'An ordinary card.' },
    ],
  } satisfies RankRule<'sevens'>,
  {
    key: 'eights',
    rank: '8',
    options: [
      { value: 'playAgain', label: 'Another go', hint: 'You take a second turn straight away.' },
      { value: 'skip', label: 'Skip', hint: 'The next player misses their turn.' },
      { value: 'none', label: 'Nothing', hint: 'An ordinary card.' },
    ],
  } satisfies RankRule<'eights'>,
  {
    key: 'jacks',
    rank: 'J',
    options: [
      { value: 'changeSuit', label: 'Change suit', hint: 'Play on anything and nominate a new suit.' },
      { value: 'reverse', label: 'Reverse', hint: 'Turns the direction of play around.' },
      { value: 'pickupFive', label: 'Pick up 5', hint: 'Black Jacks sting for 5; a red Jack blocks it.' },
      { value: 'none', label: 'Nothing', hint: 'An ordinary card.' },
    ],
  } satisfies RankRule<'jacks'>,
  {
    key: 'queens',
    rank: 'Q',
    options: [
      { value: 'skip', label: 'Skip', hint: 'The next player misses their turn.' },
      { value: 'none', label: 'Nothing', hint: 'An ordinary card.' },
    ],
  } satisfies RankRule<'queens'>,
  {
    key: 'aces',
    rank: 'A',
    options: [
      { value: 'heartsPickupFive', label: 'A♥ picks up 5', hint: 'Only the A♦ blocks it. Other Aces are ordinary.' },
      { value: 'changeSuit', label: 'Change suit', hint: 'Play on anything and nominate a new suit.' },
      { value: 'none', label: 'Nothing', hint: 'An ordinary card.' },
    ],
  } satisfies RankRule<'aces'>,
] as const;

const DRAW_OPTIONS: { value: SwitchRules['draw']; label: string; hint: string }[] = [
  {
    value: 'onePass',
    label: 'Take one, turn over',
    hint: 'Pick up a card and your turn ends — you cannot lay what you just drew.',
  },
  {
    value: 'onePlay',
    label: 'Take one, may play it',
    hint: 'Pick up a card and you may lay it straight away if it fits.',
  },
  {
    value: 'untilPlayable',
    label: 'Keep drawing',
    hint: 'Keep taking cards until one of them can be played.',
  },
];

interface ToggleProps {
  label: string;
  hint: string;
  checked: boolean;
  disabled: boolean;
  onChange: (value: boolean) => void;
}

function Toggle({ label, hint, checked, disabled, onChange }: ToggleProps) {
  return (
    <label className={`rule ${disabled ? 'rule--locked' : ''}`}>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
      />
      <span className="rule__body">
        <span className="rule__label">{label}</span>
        <span className="rule__hint">{hint}</span>
      </span>
    </label>
  );
}

export function RulesEditor({ rules, editable }: RulesEditorProps) {
  const updateRules = useStore((s) => s.updateRules);

  const set = <K extends keyof SwitchRules>(key: K, value: SwitchRules[K]) => {
    if (editable) updateRules({ [key]: value } as Partial<SwitchRules>);
  };

  const activePreset = RULE_PRESETS.find(
    (preset) => JSON.stringify(preset.rules) === JSON.stringify(rules),
  );

  const hasSuitChanger = rules.jacks === 'changeSuit' || rules.aces === 'changeSuit';
  const hasPenalty =
    rules.twos === 'pickupTwo' || rules.jacks === 'pickupFive' || rules.aces === 'heartsPickupFive';

  return (
    <section className="rules" aria-label="House rules">
      <header className="rules__header">
        <h2>House rules</h2>
        {!editable && <span className="rules__note">Only the host can change these.</span>}
      </header>

      <div className="presets">
        <span className="presets__label">Preset</span>
        <div className="presets__row">
          {RULE_PRESETS.map((preset) => (
            <button
              key={preset.id}
              type="button"
              className={`presets__button ${activePreset?.id === preset.id ? 'is-active' : ''}`}
              disabled={!editable}
              onClick={() => editable && updateRules(preset.rules)}
              title={preset.description}
            >
              {preset.name}
            </button>
          ))}
        </div>
        <p className="presets__description">
          {activePreset ? activePreset.description : 'Custom rules.'}
        </p>
      </div>

      <div className="rules__row">
        <label className="rules__number">
          <span>Starting hand</span>
          <input
            type="number"
            min={3}
            max={13}
            value={rules.startingHandSize}
            disabled={!editable}
            onChange={(event) => set('startingHandSize', Number(event.target.value))}
          />
        </label>

        <label className="rules__number">
          <span>Turn limit (s)</span>
          <input
            type="number"
            min={0}
            max={300}
            step={10}
            value={rules.turnTimeLimitSeconds}
            disabled={!editable}
            onChange={(event) => set('turnTimeLimitSeconds', Number(event.target.value))}
          />
        </label>

      </div>

      <div className="rank-rules">
        {RANK_RULES.map((rule) => {
          const current = rules[rule.key] as string;
          const active = rule.options.find((o) => o.value === current);
          return (
            <div key={rule.key} className="rank-rule">
              <span className="rank-rule__rank" aria-hidden="true">
                {rule.rank}
              </span>
              <div className="rank-rule__body">
                <div className="rank-rule__options" role="group" aria-label={`Rule for ${rule.rank}`}>
                  {rule.options.map((option) => (
                    <button
                      key={String(option.value)}
                      type="button"
                      className={`chip ${current === option.value ? 'is-active' : ''}`}
                      disabled={!editable}
                      onClick={() => set(rule.key, option.value as SwitchRules[typeof rule.key])}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
                <p className="rank-rule__hint">{active?.hint}</p>
              </div>
            </div>
          );
        })}
      </div>

      <div className="rank-rule rank-rule--wide">
        <span className="rank-rule__rank rank-rule__rank--label" aria-hidden="true">
          Draw
        </span>
        <div className="rank-rule__body">
          <div className="rank-rule__options" role="group" aria-label="Rule for drawing">
            {DRAW_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                className={`chip ${rules.draw === option.value ? 'is-active' : ''}`}
                disabled={!editable}
                onClick={() => set('draw', option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>
          <p className="rank-rule__hint">
            {DRAW_OPTIONS.find((o) => o.value === rules.draw)?.hint}
          </p>
        </div>
      </div>

      <div className="rules__grid">
        <Toggle
          label="Pick-ups stack"
          hint="Pass a pick-up along by adding another penalty card."
          checked={rules.penaltiesStack}
          disabled={!editable || !hasPenalty}
          onChange={(v) => set('penaltiesStack', v)}
        />
        <Toggle
          label="Suit changers are wild"
          hint="A suit-changing card can be played on anything."
          checked={rules.suitChangerIsWild}
          disabled={!editable || !hasSuitChanger}
          onChange={(v) => set('suitChangerIsWild', v)}
        />
        <Toggle
          label="Bluffing"
          hint="Take a card even when you could play. Costs you the turn as well."
          checked={rules.bluffing}
          disabled={!editable}
          onChange={(v) => set('bluffing', v)}
        />
        <Toggle
          label="Chain the same rank"
          hint="Lay several cards of one rank in a single turn."
          checked={rules.playMultipleSameRank}
          disabled={!editable}
          onChange={(v) => set('playMultipleSameRank', v)}
        />
        <Toggle
          label="No winning on a power card"
          hint="Your final card must be an ordinary one."
          checked={rules.cannotFinishOnPowerCard}
          disabled={!editable}
          onChange={(v) => set('cannotFinishOnPowerCard', v)}
        />
      </div>
    </section>
  );
}
